import { useEffect, useMemo, useState } from 'react';
import { getUserCertificates } from '../../firebase/certificateUtils';
import { buildCertificatePdf, triggerDownload, fetchLogoDataUrl } from './PdfGenerator';
import '../../styles/components/certificates/HistorialCertificados.css';

function HistorialCertificados({ user }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const [filters, setFilters] = useState({
    texto: '',
    empresa: '',
    tipo: '',
    codigo: ''
  });

  const formatDate = (value) => {
    if (!value) return 'N/D';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/D';
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await getUserCertificates(user?.uid || user?.id || '', user?.email || '');
        setCertificates(data || []);
      } catch (err) {
        setError('No se pudo cargar tu historial de certificados.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const downloadOrGenerate = async (cert) => {
    try {
      setError('');
      setDownloadingId(cert.id || 'local');
      if (cert.pdfUrl) {
        window.open(cert.pdfUrl, '_blank', 'noopener');
        return;
      }
      const logo = await fetchLogoDataUrl();
      const blob = buildCertificatePdf(cert, logo);
      const filename = `${cert.codigoUnico || cert.nombre || 'certificado'}-${cert.id || Date.now()}.pdf`;
      triggerDownload(blob, filename);
    } catch (err) {
      setError('No se pudo descargar/generar el PDF.');
    } finally {
      setDownloadingId('');
    }
  };

  const filteredCertificates = useMemo(() => {
    const txt = filters.texto.trim().toLowerCase();
    const emp = filters.empresa.trim().toLowerCase();
    const tip = filters.tipo.trim().toLowerCase();
    const cod = filters.codigo.trim().toLowerCase();

    return certificates.filter((c) => {
      const hayTexto = txt
        ? (c.nombre || '').toLowerCase().includes(txt) || (c.usuarioEmail || '').toLowerCase().includes(txt)
        : true;
      const hayEmp = emp ? (c.empresa || '').toLowerCase().includes(emp) : true;
      const hayTipo = tip ? (c.tipo || '').toLowerCase().includes(tip) : true;
      const hayCod = cod ? (c.codigoUnico || '').toLowerCase().includes(cod) : true;
      return hayTexto && hayEmp && hayTipo && hayCod;
    });
  }, [certificates, filters]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  if (!user) {
    return <p>Debes iniciar sesión para ver tus certificados.</p>;
  }

  return (
    <div className="certificates-history">
      <h2>Historial de certificados</h2>
      <p>Se listan los certificados asociados al usuario actual.</p>

      {loading && <p>Cargando certificados...</p>}
      {error && <p className="status-error">{error}</p>}

      {!loading && !error && certificates.length === 0 && (
        <p>No tienes certificados asociados todavía.</p>
      )}

      {!loading && (
        <div className="certificate-table-wrapper">
          <div className="filters-bar">
            <input
              name="texto"
              value={filters.texto}
              onChange={handleFilterChange}
              placeholder="Buscar por nombre o email"
            />
            <input
              name="empresa"
              value={filters.empresa}
              onChange={handleFilterChange}
              placeholder="Filtrar empresa"
            />
            <input
              name="tipo"
              value={filters.tipo}
              onChange={handleFilterChange}
              placeholder="Filtrar tipo"
            />
            <input
              name="codigo"
              value={filters.codigo}
              onChange={handleFilterChange}
              placeholder="Filtrar código"
            />
          </div>

          {filteredCertificates.length > 0 ? (
            <table className="certificate-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Empresa</th>
                  <th>Tipo</th>
                  <th>Código</th>
                  <th>Ítem</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredCertificates.map((cert) => (
                  <tr key={cert.id}>
                    <td data-label="Nombre">
                      <div className="cell-main">{cert.nombre || 'Sin nombre'}</div>
                      <div className="cell-sub">{cert.usuarioEmail || cert.userId || 'N/D'}</div>
                    </td>
                    <td data-label="Empresa">{cert.empresa || 'N/D'}</td>
                    <td data-label="Tipo">{cert.tipo || 'Certificación'}</td>
                    <td data-label="Código">{cert.codigoUnico || 'N/D'}</td>
                    <td data-label="Ítem">{cert.numeroItem || 'N/D'}</td>
                    <td data-label="Fecha">{formatDate(cert.fecha)}</td>
                    <td data-label="Estado"><span className="status-chip small">{cert.estado || 'activo'}</span></td>
                    <td data-label="Acción">
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => downloadOrGenerate(cert)}
                        disabled={downloadingId === cert.id}
                      >
                        {downloadingId === cert.id ? 'Generando...' : cert.pdfUrl ? 'Descargar' : 'Generar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">No existen registros con los filtros aplicados.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default HistorialCertificados;
